'use client';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Paragraph } from '../ui/paragraph';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQueryClient, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { ProductCard } from '../product/product-card';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Button } from '../ui/button';
import { useCallback, useState } from 'react';

export function EshopContent({ locale }: { locale: string }) {
  const searchParams = useSearchParams();
  const authToken = useCustomerStore(s => s.me?.authToken);
  const currentPageParam = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const currentPage =
    Number.isFinite(currentPageParam) && currentPageParam > 0 ? currentPageParam : 1;

  const productsQuery = useSuspenseInfiniteQuery({
    queryKey: [CacheKey.PRODUCTS, 'infinite', currentPage, authToken ?? null, locale],
    queryFn: ({ pageParam }) => {
      return fetchProducts({
        query: {
          page: pageParam,
        },
        locale,
        authToken,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const queryClient = useQueryClient();

  const lastPage = productsQuery.data?.pages[productsQuery.data?.pages.length - 1];
  const flatList = productsQuery.data?.pages.flatMap(page => page.data) ?? [];

  const [fetchTrigger, setFetchTrigger] = useState<'load-more' | 'show-all' | 'auto' | null>(null);

  const fetchNextPageSafely = useCallback(
    async (trigger: 'load-more' | 'show-all' | 'auto') => {
      if (!productsQuery.hasNextPage) return false;
      if (productsQuery.isFetchingNextPage) return false;
      setFetchTrigger(trigger);
      try {
        await productsQuery.fetchNextPage();
        return true;
      } finally {
        setFetchTrigger(null);
      }
    },
    [productsQuery],
  );

  return (
    <section
      className={cn(
        'custom-section',
        'mt-6 gap-6 pb-12 sm:mt-8 sm:gap-8 sm:pb-16 md:mt-10 md:pb-20 lg:mt-12 lg:pb-24',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-6 sm:flex-row sm:justify-between sm:gap-8',
        )}
      >
        <Paragraph size="sm" color="muted-foreground" className="w-full text-center">
          {productsQuery.data.pages[0]?.meta?.total} produktov
        </Paragraph>
      </div>
      <div
        className={cn(
          'custom-container',
          'grid grid-cols-2 gap-x-2 gap-y-6 md:grid-cols-3 md:gap-x-3 lg:grid-cols-4 lg:gap-x-6',
        )}
      >
        {flatList.map(product => (
          <ProductCard product={product} key={product.slug} />
        ))}
      </div>
      {!!productsQuery.hasNextPage && (
        <div
          className={cn(
            'flex flex-col justify-center gap-2 md:flex-row',
            'ea-category-products__load-more',
          )}
        >
          <Button
            variant="secondary"
            isLoading={productsQuery.isFetchingNextPage && fetchTrigger === 'load-more'}
            onClick={() => void fetchNextPageSafely('load-more')}
          >
            Načítať viac
          </Button>
        </div>
      )}
      <div
        className={cn(
          'custom-container',
          'flex flex-row justify-center',
          'ea-category-products__pagination',
        )}
      >
        <Pagination
          links={lastPage?.meta.links}
          onPageClicked={page => {
            if (String(page) !== String(currentPage)) return;
            queryClient.resetQueries({
              queryKey: [CacheKey.PRODUCTS, 'infinite', currentPage, authToken ?? null, locale],
              exact: true,
            });
          }}
        />
      </div>
    </section>
  );
}
